import type { AudioTrackInfo, SubtitleTrackInfo } from '../components/VideoControlsOverlay/VideoControlsOverlay';
import i18next from 'i18next';

export interface ExternalPlayback {
  currentTime(): number;
  duration(): number;
  paused(): boolean;
  play(): void;
  pause(): void;
  seek(seconds: number): void;
}

interface AvPlayCallbacks {
  onStatus: (status: 'buffering' | 'error' | null) => void;
  onTracks: (audio: AudioTrackInfo[], subtitles: SubtitleTrackInfo[]) => void;
  onTime?: (currentTime: number, duration: number) => void;
  onEnded?: () => void;
}

function extraInfo(track: any): Record<string, any> {
  try { return JSON.parse(track?.extra_info || '{}'); } catch { return {}; }
}

export function shouldUseSamsungAvPlayVod(extension: string, url: string): boolean {
  return extension.toLowerCase() === 'mkv'
    && Boolean((window as any).webapis?.avplay)
    && !url.includes('.m3u8');
}

export class SamsungAvPlayVod implements ExternalPlayback {
  private avplay: any;
  private current = 0;
  private total = 0;
  private isPaused = true;
  private resumeAfterLicenseCheck = false;
  private onLicenseBlocked = () => {
    this.resumeAfterLicenseCheck = !this.isPaused;
    if (this.resumeAfterLicenseCheck) this.pause();
  };
  private onLicenseResumed = () => {
    if (this.resumeAfterLicenseCheck) this.play();
    this.resumeAfterLicenseCheck = false;
  };

  start(url: string, seekSeconds: number, callbacks: AvPlayCallbacks): void {
    this.close();
    this.avplay = (window as any).webapis?.avplay;
    if (!this.avplay) throw new Error('Samsung AVPlay unavailable');
    callbacks.onStatus('buffering');
    try {
      this.avplay.open(url);
      window.addEventListener('auroraflux:license-verification-blocked', this.onLicenseBlocked);
      window.addEventListener('auroraflux:license-verification-resumed', this.onLicenseResumed);
      this.avplay.setListener({
        onbufferingstart: () => callbacks.onStatus('buffering'),
        onbufferingcomplete: () => callbacks.onStatus(null),
        oncurrentplaytime: (milliseconds: number) => {
          this.current = Number(milliseconds) / 1000 || 0;
          callbacks.onTime?.(this.current, this.total);
        },
        onstreamcompleted: () => callbacks.onEnded?.(),
        onerror: () => callbacks.onStatus('error'),
      });
      this.avplay.setDisplayRect(0, 0, 1920, 1080);
      this.avplay.setDisplayMethod('PLAYER_DISPLAY_MODE_LETTER_BOX');
      this.avplay.prepareAsync(() => {
        try {
          this.total = Number(this.avplay.getDuration()) / 1000 || 0;
          const tracks = this.avplay.getTotalTrackInfo?.() || [];
          const audio = tracks.filter((track: any) => track.type === 'AUDIO').map((track: any, index: number) => {
            const info = extraInfo(track);
            const lang = info.language || info.track_lang;
            return { id: track.index, name: lang || i18next.t('playback.audioTrack', { num: index + 1 }), lang };
          });
          const subtitles = tracks.filter((track: any) => track.type === 'TEXT').map((track: any, index: number) => {
            const info = extraInfo(track);
            const lang = info.language || info.track_lang;
            return { id: track.index, name: lang || i18next.t('playback.subtitleTrack', { num: index + 1 }), lang };
          });
          callbacks.onTracks(audio, subtitles);
          this.avplay.play();
          this.isPaused = false;
          this.avplay.setSilentSubtitle?.(true);
          if (seekSeconds > 0) this.seek(seekSeconds);
          callbacks.onStatus(null);
        } catch { callbacks.onStatus('error'); }
      }, () => callbacks.onStatus('error'));
    } catch (error) {
      callbacks.onStatus('error');
    }
  }

  currentTime(): number { return this.current; }
  duration(): number { return this.total; }
  paused(): boolean { return this.isPaused; }
  play(): void { this.avplay?.play(); this.isPaused = false; }
  pause(): void { this.avplay?.pause(); this.isPaused = true; }

  seek(seconds: number): void {
    const bounded = Math.max(1, Math.min(this.total || seconds, seconds));
    this.avplay?.seekTo(Math.round(bounded * 1000));
    this.current = bounded;
  }

  selectAudio(trackId: number): void { this.avplay?.setSelectTrack('AUDIO', trackId); }

  selectSubtitle(trackId: number): void {
    if (trackId < 0) this.avplay?.setSilentSubtitle?.(true);
    else {
      this.avplay?.setSilentSubtitle?.(false);
      this.avplay?.setSelectTrack('TEXT', trackId);
    }
  }

  close(): void {
    window.removeEventListener('auroraflux:license-verification-blocked', this.onLicenseBlocked);
    window.removeEventListener('auroraflux:license-verification-resumed', this.onLicenseResumed);
    if (this.avplay) {
      try { this.avplay.stop(); } catch { /* already stopped */ }
      try { this.avplay.close(); } catch { /* already closed */ }
    }
    this.avplay = null;
    this.current = 0;
    this.total = 0;
    this.isPaused = true;
    this.resumeAfterLicenseCheck = false;
  }
}
